import type {
  SketchPaletteColorOption,
  SketchPaletteOptions,
  SketchPaletteWidthOption,
  SketchToolType,
} from "@/types/stage-types.js";
import { createColorSelector } from "./subcomponents/ColorSelector/ColorSelector.js";
import { createWidthSelector } from "./subcomponents/WidthSelector/WidthSelector.js";

export interface SketchToolPaletteConfig extends SketchPaletteOptions {
  readonly onColorChange?: (color: string) => void;
  readonly onWidthChange?: (width: number) => void;
  readonly onClose?: () => void;
  readonly toolLabel?: string;
  readonly toolVariant?: SketchToolType;
}

export interface SketchToolPaletteAPI {
  readonly getElement: () => HTMLElement;
  readonly setColor: (color: string) => void;
  readonly setWidth: (width: number) => void;
  readonly setOptions: (options: SketchPaletteOptions) => void;
  readonly setTool: (payload: {
    readonly label?: string;
    readonly variant?: SketchToolType;
  }) => void;
  readonly setVisible: (visible: boolean) => void;
  readonly destroy: () => void;
}

const DEFAULT_COLORS: readonly SketchPaletteColorOption[] = [
  { id: "black", label: "Black", color: "#000000" },
  { id: "blue", label: "Blue", color: "#2563eb" },
  { id: "red", label: "Red", color: "#ef4444" },
  { id: "green", label: "Green", color: "#16a34a" },
];

const DEFAULT_WIDTHS: readonly SketchPaletteWidthOption[] = [
  { id: "thin", label: "2 px", value: 2 },
  { id: "medium", label: "4 px", value: 4 },
  { id: "thick", label: "8 px", value: 8 },
];

export const createSketchToolPalette = (
  container: HTMLElement,
  config: SketchToolPaletteConfig = {}
): SketchToolPaletteAPI => {
  const root = document.createElement("div");
  root.className = "sketch-tool-palette";
  container.appendChild(root);

  let isDestroyed = false;
  let colorSelector: ReturnType<typeof createColorSelector> | null = null;
  let widthSelector: ReturnType<typeof createWidthSelector> | null = null;

  const header = document.createElement("div");
  header.className = "sketch-tool-palette__header";
  root.appendChild(header);

  const toolLabel = document.createElement("div");
  toolLabel.className = "sketch-tool-palette__tool-label";
  header.appendChild(toolLabel);

  const closeButton = document.createElement("button");
  closeButton.className = "sketch-tool-palette__close";
  closeButton.type = "button";
  closeButton.setAttribute("aria-label", "Close palette");
  closeButton.textContent = "X";
  closeButton.addEventListener("click", () => {
    if (isDestroyed) return;
    config.onClose?.();
  });
  root.appendChild(closeButton);

  const colorGroup = document.createElement("div");
  colorGroup.className =
    "sketch-tool-palette__group sketch-tool-palette__group--colors";
  root.appendChild(colorGroup);

  const widthGroup = document.createElement("div");
  widthGroup.className =
    "sketch-tool-palette__group sketch-tool-palette__group--widths";
  root.appendChild(widthGroup);

  const applyToolLabel = (label?: string): void => {
    toolLabel.textContent = label?.trim() || "Ballpoint";
  };

  const applyToolVariant = (variant?: SketchToolType): void => {
    const nextVariant = variant ?? "ballpoint";
    root.dataset.toolVariant = nextVariant;
    root.classList.toggle(
      "sketch-tool-palette--highlighter",
      nextVariant === "highlighter"
    );
    root.classList.toggle(
      "sketch-tool-palette--eraser",
      nextVariant === "eraser"
    );
  };

  const setOptions = (options: SketchPaletteOptions): void => {
    const colors =
      options.colors && options.colors.length > 0
        ? options.colors
        : DEFAULT_COLORS;
    const widths =
      options.widths && options.widths.length > 0
        ? options.widths
        : DEFAULT_WIDTHS;
    const showColors = options.showColors !== false;
    const showWidths = options.showWidths !== false;

    colorGroup.style.display = showColors ? "" : "none";
    widthGroup.style.display = showWidths ? "" : "none";

    colorSelector?.destroy();
    colorSelector = null;
    widthSelector?.destroy();
    widthSelector = null;

    if (showColors) {
      colorSelector = createColorSelector(colorGroup, {
        options: colors,
        ...(options.initialColor !== undefined
          ? { initialColor: options.initialColor }
          : {}),
        onSelect: (option) => {
          if (isDestroyed) return;
          config.onColorChange?.(option.color);
        },
      });
    }

    if (showWidths) {
      widthSelector = createWidthSelector(widthGroup, {
        options: widths,
        ...(options.initialWidth !== undefined
          ? { initialWidth: options.initialWidth }
          : {}),
        onSelect: (option) => {
          if (isDestroyed) return;
          config.onWidthChange?.(option.value);
        },
      });
    }
  };

  const initialOptions: SketchPaletteOptions = {
    ...(config.colors ? { colors: config.colors } : {}),
    ...(config.widths ? { widths: config.widths } : {}),
    ...(config.initialColor !== undefined
      ? { initialColor: config.initialColor }
      : {}),
    ...(config.initialWidth !== undefined
      ? { initialWidth: config.initialWidth }
      : {}),
    ...(config.showColors !== undefined
      ? { showColors: config.showColors }
      : {}),
    ...(config.showWidths !== undefined
      ? { showWidths: config.showWidths }
      : {}),
  };
  setOptions(initialOptions);
  applyToolLabel(config.toolLabel);
  applyToolVariant(config.toolVariant);

  const getElement = (): HTMLElement => root;

  const setColor = (color: string): void => {
    colorSelector?.setSelectedColor(color);
  };

  const setWidth = (width: number): void => {
    widthSelector?.setSelectedWidth(width);
  };

  const setTool = (payload: {
    readonly label?: string;
    readonly variant?: SketchToolType;
  }): void => {
    applyToolLabel(payload.label);
    applyToolVariant(payload.variant);
  };

  const setVisible = (visible: boolean): void => {
    root.style.display = visible ? "flex" : "none";
  };

  const destroy = (): void => {
    if (isDestroyed) return;
    isDestroyed = true;
    colorSelector?.destroy();
    widthSelector?.destroy();
    if (root.parentElement === container) {
      container.removeChild(root);
    }
  };

  return {
    getElement,
    setColor,
    setWidth,
    setOptions,
    setTool,
    setVisible,
    destroy,
  };
};
